import { ABSTRACT_BODIES } from './bodies/abstract';
import { NATURE_BODIES } from './bodies/nature';
import { OBJECTS_BODIES } from './bodies/objects';
import { PETS_BODIES } from './bodies/pets';
import type { ImaiBody } from './types';

/** The body categories, in picker order. */
export const IMAI_CHARACTER_CATEGORIES = [
  'abstract',
  'pets',
  'nature',
  'objects',
] as const;

export type ImaiCharacterCategory = (typeof IMAI_CHARACTER_CATEGORIES)[number];

const BODIES_BY_CATEGORY: Record<
  ImaiCharacterCategory,
  Readonly<Record<string, ImaiBody>>
> = {
  abstract: ABSTRACT_BODIES,
  pets: PETS_BODIES,
  nature: NATURE_BODIES,
  objects: OBJECTS_BODIES,
};

/** Body ids of one category, in picker order. */
export function imaiCharacterIds(category: ImaiCharacterCategory): string[] {
  return Object.keys(BODIES_BY_CATEGORY[category]);
}

/** Every body id, across categories. */
export const IMAI_CHARACTER_IDS: readonly string[] =
  IMAI_CHARACTER_CATEGORIES.flatMap(imaiCharacterIds);

export function imaiCharacterBody(id: string): ImaiBody | null {
  for (const category of IMAI_CHARACTER_CATEGORIES) {
    const body = BODIES_BY_CATEGORY[category][id];
    if (body) return body;
  }
  return null;
}

export function imaiCharacterCategoryOf(
  id: string
): ImaiCharacterCategory | null {
  return (
    IMAI_CHARACTER_CATEGORIES.find(
      (category) => BODIES_BY_CATEGORY[category][id] !== undefined
    ) ?? null
  );
}

/** The colours a member can pick for the body and the accent. "Ink" adapts to the theme. */
export const IMAI_CHARACTER_INK = '#161616';
export const IMAI_CHARACTER_PALETTE = [
  IMAI_CHARACTER_INK,
  '#3E63DD',
  '#1FA2D6',
  '#12A594',
  '#2F9E5B',
  '#E3A008',
  '#C46A3E',
  '#E5484D',
  '#D6409F',
  '#8E4EC6',
] as const;

export function isImaiCharacterColor(value: string): boolean {
  return IMAI_CHARACTER_PALETTE.some((color) => color === value);
}

/** CSS for a palette colour: ink follows the theme's foreground. */
export function imaiCharacterPaint(color: string): string {
  return color === IMAI_CHARACTER_INK ? 'var(--foreground)' : color;
}

function luminance(hex: string): number {
  const channel = (offset: number) => {
    const c = Number.parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

/** Eye colour that contrasts with the colour the eyes sit on. */
export function imaiCharacterEyeInk(host: string): string {
  if (host === IMAI_CHARACTER_INK) return 'var(--background)';
  return luminance(host) > 0.36 ? IMAI_CHARACTER_INK : '#ffffff';
}
